/* ============================================================
 * N36 native cart plumbing (2026-08-15)
 *
 * Purpose: while window.N36_NATIVE_CART === true (the ?n36cart=1
 * tester cookie set in layout/theme.liquid head), make EVERY cart
 * mutation on the storefront land visibly in Dawn's native cart
 * drawer, no matter which script made it (Dawn product form,
 * n36-quick-add cards, box-builder bulk add, n36-sub-strip,
 * n36-gift-guard, anything fetch-based).
 *
 * How: one call-through wrapper around window.fetch. Any OK
 * response from /cart/add(.js), /cart/change(.js),
 * /cart/update(.js) or /cart/clear(.js) queues a section refresh:
 * GET ?sections=cart-drawer,cart-icon-bubble, then swap the same
 * DOM Dawn's own CartDrawer.renderContents()/getSectionsToRender()
 * swaps (#CartDrawer innerHTML + #cart-icon-bubble innerHTML,
 * overlay rebind, open()).
 *
 * Inert when the flag is off: the whole IIFE returns on the first
 * line. Zero listeners, zero wraps, zero globals beyond that check.
 * The wrapper ALSO re-checks the flag at call time as a belt.
 *
 * Interplay (why no double handling):
 * - Dawn's own renderers (product-form.js on PDP, cart.js /
 *   cart-drawer.js quantity edits inside the drawer) ask Shopify to
 *   bundle rendered sections WITH the mutation ("sections" in the
 *   request body) and paint them themselves. We detect that body
 *   param and skip - no second network fetch, no re-open flicker.
 * - iCart's fetch wrapper is fully muzzled by icartThemeActive=false
 *   under this same flag; the theme.liquid inline force-close hook
 *   (~line 677) is gated on !N36_NATIVE_CART. Neither runs here.
 * - n36-sub-strip's wrapper only SCHEDULES its sweep; its own
 *   change/update posts flow through us and re-render the drawer
 *   with the post-strip truth (render only, never auto-open).
 * - Note/attributes-only /cart/update.js posts (Dawn cart-note,
 *   Stellar attribute re-asserts) are skipped: nothing visible
 *   changes and a re-render would wipe the note field mid-typing.
 *
 * Public surface:
 *   window.N36Cart.refresh(opts) - re-fetch + re-render sections.
 *     opts.open truthy also opens the drawer after paint.
 *   window.N36Cart.open() - open the drawer (no-op if already open).
 *   document event 'n36cart:updated' - fired after every refresh,
 *     detail.cart = full cart JSON when cheaply available (the
 *     change/update/clear response body), else null. Also bridged
 *     from Dawn's pub-sub cartUpdate so PDP adds and in-drawer
 *     quantity edits (which we deliberately skip) still announce.
 *
 * ES5 only. No modules, no arrows, no let/const, no templates.
 * ============================================================ */
(function () {
  'use strict';

  if (window.N36_NATIVE_CART !== true) return; /* flag off: do nothing at all */
  if (window.__n36NativeCart) return;          /* idempotent: wrap once */
  window.__n36NativeCart = 1;
  if (!window.fetch || !window.Promise || !window.DOMParser) return;

  var DEBOUNCE_MS = 150;
  /* /cart/add, /cart/add.js, /cart/change(.js), /cart/update(.js),
     /cart/clear(.js), with or without a locale prefix, with or without
     a query string. Boundary group keeps /cart/adding etc. out. */
  var CART_OP_RE = /\/cart\/(add|change|update|clear)(\.js)?(?=[?#]|$)/;

  /* ---------------------------------------------------------- */
  /* drawer helpers                                             */
  /* ---------------------------------------------------------- */

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function drawerIsOpen() {
    var d = drawerEl();
    return !!(d && d.classList && d.classList.contains('active'));
  }

  function openDrawer() {
    var d = drawerEl();
    if (!d) return;
    if (d.classList.contains('active')) return; /* already open: no flicker */
    if (typeof d.open === 'function') {
      d.open(); /* Dawn CartDrawer.open(): animate/active + focus trap */
    } else {
      /* custom element not upgraded yet (cart-drawer.js still loading) */
      d.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
    }
  }

  /* ---------------------------------------------------------- */
  /* section rendering (mirrors CartDrawer.renderContents /     */
  /* getSectionsToRender in assets/cart-drawer.js)              */
  /* ---------------------------------------------------------- */

  function renderSections(sections, shouldOpen) {
    if (!sections) return;
    var parser = new DOMParser();

    /* 1) cart-drawer section -> #CartDrawer innerHTML, same as
       renderContents({id:'cart-drawer', selector:'#CartDrawer'}) */
    var drawerHtml = sections['cart-drawer'];
    if (drawerHtml) {
      var doc = parser.parseFromString(drawerHtml, 'text/html');
      var target = document.querySelector('#CartDrawer');
      var source = doc.querySelector('#CartDrawer');
      if (target && source) target.innerHTML = source.innerHTML;
      /* the on-page <cart-drawer> root sits OUTSIDE the swap; sync its
         is-empty class from the freshly rendered root so empty-state
         CSS keys correctly (Dawn's CartItems does the same via toggle) */
      var live = drawerEl();
      var fresh = doc.querySelector('cart-drawer');
      if (live && fresh) {
        if (fresh.classList.contains('is-empty')) live.classList.add('is-empty');
        else live.classList.remove('is-empty');
      }
    }

    /* 2) cart-icon-bubble section -> #cart-icon-bubble innerHTML, same
       as renderContents({id:'cart-icon-bubble'}) with the default
       '.shopify-section' source selector */
    var bubbleHtml = sections['cart-icon-bubble'];
    var bubbleTarget = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubbleTarget) {
      var bubbleDoc = parser.parseFromString(bubbleHtml, 'text/html');
      var bubbleSource = bubbleDoc.querySelector('.shopify-section');
      if (bubbleSource) bubbleTarget.innerHTML = bubbleSource.innerHTML;
    }

    /* 3) mirror renderContents' trailing setTimeout: the innerHTML swap
       replaced #CartDrawer-Overlay, so its close binding is gone; rebind,
       then open if asked */
    setTimeout(function () {
      var d = drawerEl();
      var overlay = document.getElementById('CartDrawer-Overlay');
      if (d && overlay && typeof d.close === 'function') {
        overlay.addEventListener('click', function () { d.close(); });
      }
      if (shouldOpen) openDrawer();
    });
  }

  /* ---------------------------------------------------------- */
  /* refresh queue (coalesces bursts within DEBOUNCE_MS)        */
  /* ---------------------------------------------------------- */

  var queue = { timer: null, open: false, cart: null };

  function doRefresh(shouldOpen, cartJson) {
    var base = (window.routes && window.routes.root_url) || '/';
    var url = base + (base.indexOf('?') === -1 ? '?' : '&') + 'sections=cart-drawer,cart-icon-bubble';
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        renderSections(sections, shouldOpen);
        dispatchUpdated(cartJson);
      })
      ['catch'](function () { /* plumbing must never take the page down */ });
  }

  function queueRefresh(shouldOpen, cartJson) {
    queue.open = queue.open || !!shouldOpen;
    if (cartJson) queue.cart = cartJson;
    if (queue.timer) return; /* trailing coalesce: one paint per burst */
    queue.timer = setTimeout(function () {
      var open = queue.open;
      var cart = queue.cart;
      queue.timer = null;
      queue.open = false;
      queue.cart = null;
      doRefresh(open, cart);
    }, DEBOUNCE_MS);
  }

  /* ---------------------------------------------------------- */
  /* n36cart:updated event                                      */
  /* ---------------------------------------------------------- */

  function reallyDispatch(cart) {
    try {
      var detail = { cart: cart || null };
      var ev;
      if (typeof CustomEvent === 'function') {
        ev = new CustomEvent('n36cart:updated', { bubbles: true, detail: detail });
      } else {
        ev = document.createEvent('CustomEvent');
        ev.initCustomEvent('n36cart:updated', true, false, detail);
      }
      document.dispatchEvent(ev);
    } catch (e) { /* never throw from plumbing */ }
  }

  /* Consumers (cow bar, pairs-with) need cart JSON to render without
     issuing their own racy fallback fetches. When the triggering response
     body was not the cart (add.js returns items), fetch /cart.js here via
     the NATIVE fetch (never the wrapper: /cart.js is a read, but going
     around the wrapper makes loops structurally impossible) and dispatch
     with it. On failure dispatch with null so listeners still fire. */
  function dispatchUpdated(cart) {
    if (cart && typeof cart.total_price === 'number') { reallyDispatch(cart); return; }
    try {
      nativeFetch.call(window, '/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { reallyDispatch(c && typeof c.total_price === 'number' ? c : null); })
        .catch(function () { reallyDispatch(null); });
    } catch (e) { reallyDispatch(null); }
  }

  /* Dawn-internal renders (PDP product form, in-drawer quantity edits)
     are skipped by the wrapper below, but they publish on Dawn's pub-sub;
     bridge those so 'n36cart:updated' fires for EVERY cart change. */
  function bridgePubSub() {
    try {
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (event) {
          var cart = event && event.cartData;
          dispatchUpdated(cart && typeof cart.item_count === 'number' ? cart : null);
        });
        return true;
      }
    } catch (e) {}
    return false;
  }
  if (!bridgePubSub()) {
    document.addEventListener('DOMContentLoaded', bridgePubSub);
  }

  /* ---------------------------------------------------------- */
  /* request classification                                     */
  /* ---------------------------------------------------------- */

  /* Dawn-style callers put "sections" in the mutation body and paint
     the returned sections themselves (product-form.js on PDP adds,
     cart.js/cart-drawer.js on quantity edits). Skip those: rendering
     again would double-fetch and flicker-reopen the drawer. */
  function requestHandlesOwnRender(init) {
    try {
      if (!init || !init.body) return false;
      var b = init.body;
      if (typeof b === 'string') return b.indexOf('"sections"') !== -1;
      if (window.FormData && b instanceof window.FormData && typeof b.get === 'function') {
        return b.get('sections') != null;
      }
    } catch (e) {}
    return false;
  }

  /* Note/attributes-only /cart/update.js: no line changes, nothing the
     drawer shows. Re-rendering would wipe the cart-note textarea while
     the customer types (Dawn's cart-note posts {note} per debounce) and
     would churn on Stellar's cart-attribute re-asserts. */
  function isCosmeticUpdate(op, init) {
    if (op !== 'update') return false;
    try {
      var b = init && init.body;
      if (typeof b === 'string' && b.charAt(0) === '{') {
        return b.indexOf('"updates"') === -1;
      }
    } catch (e) {}
    return false;
  }

  /* ---------------------------------------------------------- */
  /* First Box Gift rescue (2026-08-21)                         */
  /* ---------------------------------------------------------- */

  /* The n36-gift-guard checkout function blocks the $0 First Box Gift
     whenever its qualifier (Farm Box components / Latitude Half-Full share)
     is gone — at CART MUTATION time too. So a plain "remove the box" or
     "remove the share" tap would 422 with the gift still aboard (the trap
     that had the orphan block turned off on 2026-08-05). Two fixes here:
     (1) the box card's trash control zeroes the gift inside the same atomic
         /cart/update.js (giftLineKeys below), and
     (2) any change/update that earns the gift error is replayed ONCE as a
         single /cart/update.js that carries the original intent PLUS the
         gift lines at 0 (giftRescue). The caller receives the replay's
         response, so Dawn's own section painting keeps working. */
  var GIFT_VARIANT_ID = 50995899629815;
  var GIFT_PROP = '_n36_free_steak';
  var LATITUDE_VARIANTS = ['50201380618487', '50201380585719'];
  var GIFT_ERR_RE = /First Box Gift/i;

  function isGiftLine(i) {
    return !!(i && ((i.properties && i.properties[GIFT_PROP]) || i.variant_id === GIFT_VARIANT_ID));
  }
  function giftLineKeys(cart) {
    var keys = [];
    ((cart && cart.items) || []).forEach(function (i) { if (isGiftLine(i) && i.key) keys.push(i.key); });
    return keys;
  }
  /* does the cart still hold a Latitude Half/Full share on a plan? (then the
     gift is legitimately earned and must stay) */
  function hasLatitude(cart) {
    return ((cart && cart.items) || []).some(function (i) {
      return LATITUDE_VARIANTS.indexOf(String(i.variant_id)) !== -1 && i.selling_plan_allocation;
    });
  }
  function fetchCart() {
    return nativeFetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; });
  }
  /* best-effort body parse: JSON string, URLSearchParams, FormData, or a
     form-encoded string. Returns a plain object or null. */
  function parseBody(init) {
    var b = init && init.body;
    if (b == null) return null;
    try {
      if (typeof b === 'string') {
        if (b.charAt(0) === '{') return JSON.parse(b);
        var o = {}; new URLSearchParams(b).forEach(function (v, k) { o[k] = v; }); return o;
      }
      if (window.URLSearchParams && b instanceof window.URLSearchParams) {
        var o2 = {}; b.forEach(function (v, k) { o2[k] = v; }); return o2;
      }
      if (window.FormData && b instanceof window.FormData) {
        var o3 = {}; b.forEach(function (v, k) { o3[k] = v; }); return o3;
      }
    } catch (e) {}
    return null;
  }
  /* Build the one-shot replay. Resolves to a Response (the replay's) or null
     when the intent cannot be expressed safely (then the caller gets the
     original 422 and the stale-index self-heal runs as before). */
  function giftRescue(op, init) {
    var body = parseBody(init);
    if (!body) return Promise.resolve(null);
    if (body.selling_plan !== undefined || body.properties !== undefined) return Promise.resolve(null);
    return fetchCart().then(function (cart) {
      if (!cart || !cart.items) return null;
      var gifts = giftLineKeys(cart);
      if (!gifts.length) return null;
      var updates = {};
      if (op === 'update') {
        if (!body.updates || typeof body.updates !== 'object' || Array.isArray(body.updates)) return null;
        Object.keys(body.updates).forEach(function (k) { updates[k] = body.updates[k]; });
      } else if (op === 'change') {
        if (body.quantity === undefined) return null;
        var key = null;
        if (body.id !== undefined) {
          var id = String(body.id);
          cart.items.some(function (i) { if (i.key === id || String(i.variant_id) === id) { key = i.key; return true; } return false; });
        } else if (body.line !== undefined) {
          var idx = parseInt(body.line, 10);
          if (!(idx >= 1) || !cart.items[idx - 1]) return null;
          key = cart.items[idx - 1].key;
        }
        if (!key) return null;
        updates[key] = parseInt(body.quantity, 10);
        if (isNaN(updates[key])) return null;
      } else {
        return null;
      }
      gifts.forEach(function (k) { if (!(k in updates)) updates[k] = 0; });
      var payload = { updates: updates };
      if (body.sections !== undefined) payload.sections = body.sections;
      if (body.sections_url !== undefined) payload.sections_url = body.sections_url;
      return nativeFetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
    })['catch'](function () { return null; });
  }

  /* ---------------------------------------------------------- */
  /* the fetch wrap (call-through, once, flag-checked per call) */
  /* ---------------------------------------------------------- */

  var nativeFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var rawPromise = nativeFetch.apply(this, args);
    if (window.N36_NATIVE_CART !== true) return rawPromise; /* belt: call-time flag check */

    var init = args[1];
    var requestUrl = '';
    try { requestUrl = String(args[0] && args[0].url ? args[0].url : args[0]); } catch (e) {}

    /* gift rescue: a 422 on change/update carrying the gift-guard message is
       replayed once (see giftRescue); the caller then receives the replay's
       response instead of the 422. Anything else passes through untouched. */
    var promise = rawPromise.then(function (response) {
      try {
        if (!response || response.status !== 422 || !response.clone) return response;
        var m = CART_OP_RE.exec(String(response.url || requestUrl));
        if (!m || (m[1] !== 'change' && m[1] !== 'update')) return response;
        return response.clone().json().then(function (j) {
          var msg = (j && (j.message || j.description)) || '';
          if (!GIFT_ERR_RE.test(String(msg))) return response;
          return giftRescue(m[1], init).then(function (replay) { return replay || response; });
        }, function () { return response; });
      } catch (e) { return response; }
    });

    /* observe only; the caller gets `promise` (the original response, or the
       rescue replay's) */
    promise.then(function (response) {
      try {
        if (!response) return;
        if (!response.ok) {
          /* SELF-HEAL (flip night 2026-08-21): a rejected change/update usually
             means the drawer's line indexes went stale (Dawn posts `line` = the
             row's index; a box add / Recharge reassembly shifts lines under it →
             Shopify: "line parameter is invalid"). Re-render from the real cart
             so the next tap lands. Never opens the drawer. */
          if (CART_OP_RE.test(String(response.url || requestUrl))) queueRefresh(false, null);
          return;
        }
        var match = CART_OP_RE.exec(String(response.url || requestUrl));
        if (!match) return;
        var op = match[1];
        if (requestHandlesOwnRender(init)) {
          /* Dawn paints this one itself — no render from us. But consumers
             (gauge, pairs-with) still need the update event, and the pub-sub
             bridge only works when the theme loads Dawn's pubsub.js, so
             dispatch here unconditionally. change/update bodies ARE the
             cart JSON; adds dispatch null and dispatchUpdated fetches it. */
          if (op !== 'add' && response.clone) {
            response.clone().json().then(
              function (cart) { dispatchUpdated(cart && typeof cart.item_count === 'number' ? cart : null); },
              function () { dispatchUpdated(null); }
            );
          } else {
            dispatchUpdated(null);
          }
          return;
        }
        if (isCosmeticUpdate(op, init)) return;

        /* adds open the drawer (unless it is already open: render only);
           change/update/clear re-render in place and never force it open,
           so background fixers (sub-strip, gift-guard, box purges) stay
           invisible */
        var wantOpen = op === 'add' && !drawerIsOpen();

        /* change/update/clear response bodies ARE the full cart JSON;
           clone it for the event so listeners get state for free */
        if (op !== 'add' && response.clone) {
          response.clone().json().then(
            function (cart) {
              queueRefresh(wantOpen, cart && typeof cart.item_count === 'number' ? cart : null);
            },
            function () { queueRefresh(wantOpen, null); }
          );
        } else {
          queueRefresh(wantOpen, null);
        }
      } catch (e) { /* observation errors must never surface to the caller */ }
    }, function () { /* network failure: caller handles it; nothing for us */ });

    return promise;
  };

  /* ---------------------------------------------------------- */
  /* XHR observer (flip-night 2026-08-21)                       */
  /* ---------------------------------------------------------- */

  /* Some apps mutate the cart over XMLHttpRequest, not fetch — iCart's
     loader (still bound to the live theme at the flip) grabbed the PDP
     form submit ahead of our interceptor and POSTed /cart/add.js?ref_icart=true
     over XHR, so the fetch wrap above never saw the add and the drawer never
     opened. Observe XHR cart ops the same way: classify by URL, skip
     own-render (body carries "sections") and cosmetic updates, then queue the
     identical refresh. Observation only — the request is untouched. */
  (function () {
    try {
      var XP = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
      if (!XP || XP.__n36CartObserved) return;
      XP.__n36CartObserved = true;
      var nativeOpen = XP.open, nativeSend = XP.send;
      XP.open = function (method, url) {
        try { this.__n36Url = String(url || ''); this.__n36Method = String(method || '').toUpperCase(); } catch (e) {}
        return nativeOpen.apply(this, arguments);
      };
      XP.send = function (body) {
        var xhr = this;
        try {
          if (window.N36_NATIVE_CART === true && xhr.__n36Method === 'POST') {
            var m = CART_OP_RE.exec(xhr.__n36Url || '');
            if (m) {
              var op = m[1];
              var ownRender = false, cosmetic = false;
              try {
                if (typeof body === 'string') { ownRender = body.indexOf('"sections"') !== -1 || body.indexOf('sections=') !== -1; cosmetic = (op === 'update' && body.charAt(0) === '{' && body.indexOf('"updates"') === -1); }
                else if (body && window.FormData && body instanceof window.FormData && typeof body.get === 'function') { ownRender = body.get('sections') != null; }
              } catch (e) {}
              xhr.addEventListener('loadend', function () {
                try {
                  if (window.N36_NATIVE_CART !== true) return;
                  if (!(xhr.status >= 200 && xhr.status < 300)) { queueRefresh(false, null); return; } /* self-heal stale indexes */
                  var cart = null;
                  if (op !== 'add') { try { var j = JSON.parse(xhr.responseText); if (j && typeof j.item_count === 'number') cart = j; } catch (e) {} }
                  if (ownRender) { dispatchUpdated(cart); return; }
                  if (cosmetic) return;
                  queueRefresh(op === 'add' && !drawerIsOpen(), cart);
                } catch (e) { /* never throw from plumbing */ }
              });
            }
          }
        } catch (e) {}
        return nativeSend.apply(this, arguments);
      };
    } catch (e) { /* XHR patching is best-effort */ }
  })();

  /* ---------------------------------------------------------- */
  /* plain-form adds (custom N36 PDP templates)                 */
  /* ---------------------------------------------------------- */

  /* The custom beef/kit PDP buy forms are plain HTML posts to /cart/add;
     on the live site iCart's hijack catches them, but under this flag iCart
     is muzzled and the browser would navigate to /cart. Intercept at the
     document level (capture phase, so we run even if the form is added
     later), convert to a fetch through our own wrapper (which refreshes and
     opens the drawer), and leave Dawn's <product-form> elements alone: they
     preventDefault and render on their own. */
  /* BUBBLE phase + defaultPrevented check (flip night 2026-08-21): pages that
     handle their own buy form (the Latitude Club section posts membership +
     First Box Gift atomically and preventDefaults) must not ALSO get our add —
     under iCart its hijack swallowed those submits; under the native cart both
     ran and a Half Share landed twice. Bubble-phase delegation on document still
     catches forms added later; a page handler that preventDefaults wins. */
  document.addEventListener('submit', function (e) {
    try {
      if (window.N36_NATIVE_CART !== true) return;
      if (e.defaultPrevented) return; /* the page owns this form */
      var f = e.target;
      if (!f || f.nodeName !== 'FORM') return;
      var action = f.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      if (f.closest('product-form')) return; /* Dawn owns these */
      if (f.hasAttribute('data-n36-self-handled')) return; /* explicit opt-out */
      e.preventDefault();
      var btn = f.querySelector('[type="submit"], button[name="add"]');
      if (btn) btn.setAttribute('aria-disabled', 'true');
      var fd = new FormData(f);
      window.fetch('/cart/add.js', { method: 'POST', body: fd })
        .then(function (r) { return r.json(); })
        ['catch'](function () { /* wrapper already observed; nothing else */ })
        .then(function () { if (btn) btn.removeAttribute('aria-disabled'); });
    } catch (err) { /* fall through to native submit on any error */ }
  }, false);

  /* ---------------------------------------------------------- */
  /* bundle remove links (Recharge box cards)                   */
  /* ---------------------------------------------------------- */

  /* The grouped box card's trash control is a plain <a> to
     /cart/update?updates[key]=0&... — one atomic call zeroing every child
     line (the checkout mutation guard requires the batch). Under the flag,
     following it would navigate to /cart; convert drawer clicks to a fetch
     through our wrapper so the drawer re-renders in place. Clicks on the
     /cart PAGE are converted too (2026-08-21): the plain navigation cannot
     carry the gift line, and Shopify answers a validation-rejected HTML
     /cart/update with a bare 422 error page, so the page path posts the same
     atomic update and reloads. Any parse failure falls through to the link,
     which still works. */
  document.addEventListener('click', function (e) {
    try {
      if (window.N36_NATIVE_CART !== true) return;
      var a = e.target && e.target.closest && e.target.closest('a.bundle-remove-button');
      if (!a) return;
      var inDrawer = !!a.closest('#CartDrawer');
      var href = a.getAttribute('href') || '';
      if (href.indexOf('/cart/update') === -1) return;
      var updates = {};
      var found = false;
      new URL(href, window.location.origin).searchParams.forEach(function (v, k) {
        var m = k.match(/^updates\[(.+)\]$/);
        if (m) { updates[m[1]] = 0; found = true; }
      });
      if (!found) return; /* unexpected shape: let the link do its thing */
      e.preventDefault();
      a.setAttribute('aria-disabled', 'true');
      /* the First Box Gift rides on this box: drop it in the SAME update
         (the gift guard rejects a mutation that strands it) unless a
         Latitude share still earns it. Cart read failure → plain removal;
         the wrapper's gift rescue then covers the 422. */
      fetchCart().then(function (cart) {
        if (cart && !hasLatitude(cart)) {
          giftLineKeys(cart).forEach(function (k) { updates[k] = 0; });
        }
      })['catch'](function () {}).then(function () {
        return window.fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      })['catch'](function () { /* wrapper observed; card stays for retry */ })
        .then(function (r) {
          a.removeAttribute('aria-disabled');
          /* the /cart page has no drawer to re-render: reload to repaint */
          if (!inDrawer && r && r.ok) window.location.reload();
        });
    } catch (err) { /* fall through to native navigation on any error */ }
  }, true);

  /* ---------------------------------------------------------- */
  /* remove an applied discount code (the drawer "x" button)     */
  /* ---------------------------------------------------------- */

  /* Shopify honors an empty `discount` on /cart/update.js (proven live
     2026-08-21) to clear the cart's discount code. Delegated + document-level
     so it survives every drawer re-render (an inline handler in the injected
     section HTML would never execute). The wrapper treats a discount-only
     update as cosmetic, so refresh the drawer explicitly. */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('.n36-remove-discount');
    if (!b) return;
    e.preventDefault();
    if (b.getAttribute('aria-busy') === 'true') return;
    b.setAttribute('aria-busy', 'true');
    b.style.opacity = '.25';
    nativeFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: '' })
    }).then(function () { queueRefresh(false, null); })
      ['catch'](function () { b.removeAttribute('aria-busy'); b.style.opacity = '.55'; });
  }, false);

  /* ---------------------------------------------------------- */
  /* public API                                                 */
  /* ---------------------------------------------------------- */

  window.N36Cart = {
    /* N36Cart.refresh() or N36Cart.refresh({open:true, cart:cartJson}) */
    refresh: function (opts) {
      queueRefresh(!!(opts && opts.open), (opts && opts.cart) || null);
    },
    open: openDrawer
  };
})();
